import { useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import './styles/Home.scss'
import useWindowDimensions from '../../hooks/size';
import { ApiHelper } from '../../apiHelper/ApiHelper';
import { resolveInstagramImage } from '../../Constants';
import { Pricing, formatPrice, paymentModelLabel } from '../../components/ProductCatalog';
import { landingOrder, productInfo, productNames } from '../../products';
import PromoCountdown, { promoDeadline } from '../../components/PromoCountdown';
import Seo from '../../components/Seo';

// The landing cards render from the shared product catalogue (src/products.ts) so their
// names and copy can never drift from the /nakup catalogue. Each card deep-links to its
// real product on /nakup and shows the live price from the pricing API.
const products = landingOrder.map((id) => ({ id, title: productNames[id], ...productInfo[id] }));


const steps = [
    { n: "1", title: "Vyber si kurz", text: "Zvol variantu přípravy, která Ti sedne." },
    { n: "2", title: "Zarezervuj místo", text: "Počet míst ve skupinkách je omezený." },
    { n: "3", title: "Začni v říjnu", text: "Výuka běží každý týden od října do května." },
    { n: "4", title: "Uspěj u přijímaček", text: "Provedeme Tě až na vysněnou fakultu." },
];

// Two flows students most often ask about — kept honest with what the app does:
// the checkout (first payment → instant access) and term voting after purchase.
const flows = [
    {
        title: "Jak probíhá nákup",
        lead: "Od výběru plánu k první lekci vede jen pár klidných kroků.",
        items: [
            { n: "1", text: "Vybereš si plán přípravy, který Ti sedne." },
            { n: "2", text: "Bezpečně zaplatíš online — rovnou, nebo pohodlně po platbách." },
            { n: "3", text: "Přístup ke kurzu i všem materiálům máš okamžitě." },
        ],
    },
    {
        title: "Jak se volí termíny lekcí",
        lead: "U kurzů s lektorem si čas výuky neurčujeme za Tebe — vybíráš si ho sám.",
        items: [
            { n: "1", text: "Po nákupu kurzu uvidíš nabídku možných termínů (den a čas)." },
            { n: "2", text: "Klikneš na termín, který Ti nejvíc vyhovuje." },
            { n: "3", text: "Podle hlasování Ti přiřadíme pravidelný čas lekcí s lektorem." },
        ],
    },
];

// Answers to what students ask most — honest, no fabricated numbers. Rendered
// visibly in the FAQ section and mirrored into FAQPage JSON-LD (see <Seo>) so the
// structured data always matches the on-page copy.
const faqs = [
    {
        q: "Pro koho je příprava určená?",
        a: "Pro studenty, kteří se hlásí na lékařské fakulty a chtějí zvládnout přijímačky z fyziky, chemie a biologie. Ať začínáš od základů, nebo si jen doplňuješ mezery, tempo přizpůsobíme Tobě.",
    },
    {
        q: "Jak výuka probíhá?",
        a: "Živé online lekce v malých skupinkách (max. 8 studentů) vedené mediky, doplněné kompletními materiály, videi s řešenými příklady a testy — vše přehledně na jednom místě.",
    },
    {
        q: "Na které fakulty mě připravíte?",
        a: "Na lékařské fakulty po celé ČR — v Praze, Brně i jinde. Příprava pokrývá fyziku, chemii a biologii v rozsahu, který přijímačky vyžadují.",
    },
    {
        q: "Zvládnu to, i když jsem pozadu?",
        a: "Ano. Procházíme všechny předměty od úplných základů a zaměříme se přesně na Tvé slabší stránky. Pomůžeme i s přípravou na ústní pohovory.",
    },
    {
        q: "Kolik příprava stojí a jak se platí?",
        a: "Máme několik plánů — od samostudia po kompletní kurz s lektorem. Aktuální ceny najdeš u jednotlivých plánů a u vybraných z nich můžeš platit pohodlně po platbách.",
    },
    {
        q: "Nejsem si jistý, jestli je kurz pro mě. Co teď?",
        a: "Domluv si nezávaznou konzultaci zdarma. Probereme, jak na tom jsi, co Tě u přijímaček čeká, a společně najdeme nejlepší postup — bez závazků.",
    },
];

type IgPost = { url: string; image: string };

// Fallback shown if the API is unavailable — admins manage the live selection
// in Administrace → Nastavení (thumbnails are resolved from the post link).
const DEFAULT_IG: IgPost[] = [
    { url: "https://www.instagram.com/p/DCZrHFqRQdv/", image: "/images/pages/igPosts/1.png" },
    { url: "https://www.instagram.com/p/DCZr_jnsS3E/", image: "/images/pages/igPosts/2.png" },
    { url: "https://www.instagram.com/p/DCZslSUsDsG/", image: "/images/pages/igPosts/3.png" },
    { url: "https://www.instagram.com/p/DCb6RWFMSKG/", image: "/images/pages/igPosts/4.png" },
];

export default function Home() {
    const navigate = useNavigate();
    const { width } = useWindowDimensions();
    const api = new ApiHelper();
    const [igPosts, setIgPosts] = useState<IgPost[]>(DEFAULT_IG);
    const [pricing, setPricing] = useState<Pricing[]>([]);

    useEffect(() => {
        loadInstagram();
        loadPricing();
    }, []);

    async function loadInstagram() {
        try {
            const posts: { url: string; image?: string }[] = await (await api.siteSettingsApi.getInstagramPostsList()).json();
            const resolved = posts.filter(p => p.url && p.image).map(p => ({ url: p.url, image: resolveInstagramImage(p.image) }));
            if (resolved.length) setIgPosts(resolved);
        } catch { /* keep defaults */ }
    }

    async function loadPricing() {
        try {
            setPricing(await (await api.pricingApi.getPricingList()).json());
        } catch { /* no price shown if unavailable — never fabricate */ }
    }

    const priceFor = (id: number) => pricing.find(p => p.product === id);
    const goToProducts = () => document.getElementById("kurzy")?.scrollIntoView({ behavior: "smooth" });
    const promoEnds = promoDeadline(pricing);

    return (
        <div className="home">
            <Seo canonicalPath="/">
                <script type="application/ld+json">{JSON.stringify({
                    '@context': 'https://schema.org',
                    '@type': 'FAQPage',
                    mainEntity: faqs.map((f) => ({
                        '@type': 'Question',
                        name: f.q,
                        acceptedAnswer: { '@type': 'Answer', text: f.a },
                    })),
                })}</script>
            </Seo>
            <header className="hero">
                <div className="text">
                    <h1>
                        Přijímačky na medicínu <br />
                        <span className="accent">zvládneš s námi.</span>
                    </h1>
                    <p className="sub">
                        Prémiová příprava v malých skupinkách vedená mediky — kompletní materiály,
                        živé online lekce a osobní vedení až k přijetí na lékařskou fakultu.
                    </p>
                    <div className="heroPills">
                        <span>Malé skupinky · max. 8</span>
                        <span>Učí Tě medici</span>
                        <span>Online napříč ČR</span>
                    </div>
                    <div className="heroCtas">
                        <button className="button" onClick={() => navigate("/nakup")}><p>Chci kurz</p></button>
                        <button className="button ghost" onClick={goToProducts}><p>Prohlédnout kurzy</p></button>
                    </div>
                    <p className="heroProof">
                        Učí Tě medici, kteří si přijímačkami sami prošli — připravili už desítky studentů.
                    </p>
                </div>
                <div className="image">
                    <picture>
                        <source srcSet="images/pages/main.webp" type="image/webp" />
                        <img src="images/pages/main.png" alt="Aplikace MyMed s přehledem lekcí fyziky pro přípravu na přijímačky na medicínu" width={730} height={950} decoding="async" />
                    </picture>
                </div>
            </header>

            <section className="trustStrip">
                <div className="item">
                    <h2>Roky zkušeností</h2>
                    <p>s výukou a přípravou na přijímačky</p>
                </div>
                <div className="item">
                    <h2>Desítky odučených studentů</h2>
                    <p>ve fyzice, chemii i biologii</p>
                </div>
                <div className="item">
                    <h2>Úspěšní absolventi</h2>
                    <p>naši studenti studují na lékařských fakultách</p>
                </div>
            </section>

            <section className="products" id="kurzy">
                <p className="eyebrow">NAŠE KURZY</p>
                <h2 className="sectionTitle center">Vyber si, jak se chceš připravit</h2>
                <p className="sectionLead">
                    Ať už chceš pevné vedení lektora, nebo se učíš nejradši sám — máme variantu,
                    která sedne přesně Tobě.
                </p>
                <p className="scarcityNote">Omezený počet míst ve skupinkách (max. 8) · kurzy startují v říjnu</p>

                {promoEnds &&
                    <div className="promoBanner">
                        <PromoCountdown deadline={promoEnds} />
                    </div>
                }

                <div className="productGrid">
                    {products.map((p) => {
                        const pr = priceFor(p.id);
                        return (
                            <article key={p.title} className={"card" + (p.recommended ? " featured" : "")}>
                                {p.ribbon && <span className="ribbon">{p.ribbon}</span>}
                                <h3>{p.title}</h3>
                                <p className="lead">{p.lead}</p>
                                {pr && (
                                    <div className="cardPrice">
                                        {pr.discountedPrice != null &&
                                            <span className="anchor">{formatPrice(pr.basePrice)}</span>
                                        }
                                        <span className="now">{formatPrice(pr.effectivePrice)}</span>
                                        {pr.paymentModel === 2 && <span className="per">/ měsíc</span>}
                                        <span className="model">{paymentModelLabel(pr)}</span>
                                    </div>
                                )}
                                <ul>
                                    {p.features.map((f) => (
                                        <li key={f}>
                                            <img src="images/pages/check.png" alt="" width={30} height={22} />
                                            <span>{f}</span>
                                        </li>
                                    ))}
                                </ul>
                                {pr && !pr.available
                                    ? <div className="cardCta notForSale">Nyní nelze zakoupit</div>
                                    : <button
                                        className={"cardCta" + (p.recommended ? "" : " ghost")}
                                        onClick={() => navigate("/nakup?plan=" + p.id)}
                                    >
                                        Vybrat plán
                                    </button>
                                }
                            </article>
                        );
                    })}
                </div>
            </section>

            <section className="materials">
                <div className="copy">
                    <p className="eyebrow">VŠE NA JEDNOM MÍSTĚ</p>
                    <h2 className="sectionTitle">Materiály, které dávají smysl</h2>
                    <p>
                        Kromě výuky s lektorem máš k dispozici online systém se <b>všemi studijními materiály</b>.
                        Najdeš tu přehledně zpracované texty k jednotlivým kapitolám, opakovací testy,
                        videa s řešenými příklady a spoustu šikovných přehledů — od vzorečků
                        z fyziky po metabolismy v chemii.
                    </p>
                    <button className="button" onClick={() => navigate("/nakup")}><p>Chci kurz</p></button>
                </div>
                <div className="photos">
                    <picture>
                        <source srcSet="images/pages/main2.webp" type="image/webp" />
                        <img className="a" src="images/pages/main2.JPG" alt="Tištěné i online studijní materiály MyMed k přijímačkám z fyziky, chemie a biologie" width={700} height={467} loading="lazy" />
                    </picture>
                    <picture>
                        <source srcSet="images/pages/main1.webp" type="image/webp" />
                        <img className="b" src="images/pages/main1.JPG" alt="Pracovní sešit s testovými otázkami, barevné přehledy a tablet se studijními materiály MyMed" width={675} height={451} loading="lazy" />
                    </picture>
                </div>
            </section>

            <section className="whyUs">
                <h2 className="sectionTitle center">S námi to zvládneš!</h2>
                <div className="whyGrid">
                    <div className="why">
                        <h3>Roky zkušeností s výukou</h3>
                        <p>
                            Výuce studentů se věnujeme už několik let a máme bohaté zkušenosti s přípravou
                            na přijímačky na lékařské fakulty. Sami jsme si přijímačkami prošli, a tak přesně
                            víme, na co se zaměřit.
                        </p>
                    </div>
                    <div className="why">
                        <h3>Výuka v malých skupinách</h3>
                        <p>
                            Záleží nám na individuálním přístupu, a proto veškerá výuka probíhá v malých
                            skupinkách. Díky tomu se zaměříme přesně na to, co potřebuješ probrat, a nabídneme
                            Ti tu nejefektivnější přípravu.
                        </p>
                    </div>
                    <div className="why">
                        <h3>Provedeme Tě celou přípravou</h3>
                        <p>
                            Projdeme s Tebou všechny předměty od úplných základů až do konce, pomůžeme
                            i s přípravou na ústní pohovory a naučíme Tě vše, co k přijímačkám potřebuješ.
                        </p>
                    </div>
                </div>
                <picture>
                    <source srcSet={width > 600 ? "images/pages/figures.webp" : "images/pages/figuresSmall.webp"} type="image/webp" />
                    <img
                        className="figures"
                        src={width > 600 ? "images/pages/figures.png" : "images/pages/figuresSmall.png"}
                        width={width > 600 ? 2149 : 1127}
                        height={width > 600 ? 874 : 850}
                        alt=""
                        loading="lazy"
                    />
                </picture>
            </section>

            <section className="tutors">
                <h2 className="sectionTitle center">Kdo ručí za výuku</h2>
                <div className="tutorLead">
                    <div className="photo">
                        <img src="/images/pages/contacts/photos/paja.jpeg" alt="Pája – hlavní lektorka MyMed a studentka medicíny" width={440} height={440} loading="lazy" />
                    </div>
                    <div className="info">
                        <span className="badge">Hlavní lektorka</span>
                        <h3>Pája</h3>
                        <p className="cred">studentka medicíny</p>
                        <p className="bio">
                            Sama si přijímačkami prošla a dnes studuje medicínu — přesně ví, co Tě
                            u zkoušek čeká. Provede Tě celou přípravou krok za krokem a poradí,
                            na co si dát pozor.
                        </p>
                    </div>
                </div>
            </section>

            <section className="social">
                <h2 className="header">Sleduj nás na Instagramu</h2>
                <div className="postLine">
                    {igPosts.map((post, i) => (
                        <a key={i} href={post.url} className="post" target="_blank" rel="noreferrer" aria-label="Příspěvek MyMed na Instagramu">
                            <img src={post.image} alt="" referrerPolicy="no-referrer" loading="lazy" />
                        </a>
                    ))}
                </div>
            </section>

            <section className="steps">
                <h2 className="sectionTitle center">Jak to probíhá</h2>
                <div className="stepGrid">
                    {steps.map((s) => (
                        <div key={s.n} className="step">
                            <span className="num">{s.n}</span>
                            <h3>{s.title}</h3>
                            <p>{s.text}</p>
                        </div>
                    ))}
                </div>
            </section>

            <section className="flows">
                <p className="eyebrow">NEŽ SE PUSTÍŠ DO TOHO</p>
                <h2 className="sectionTitle center">Nákup a termíny lekcí jednoduše</h2>
                <p className="sectionLead">
                    Ať víš přesně, do čeho jdeš — takhle vypadá nákup a takhle si po něm
                    zvolíš termín výuky.
                </p>

                <div className="flowGrid">
                    {flows.map((f) => (
                        <article key={f.title} className="flowCard">
                            <h3>{f.title}</h3>
                            <p className="lead">{f.lead}</p>
                            <ol>
                                {f.items.map((it) => (
                                    <li key={it.n}>
                                        <span className="num">{it.n}</span>
                                        <span className="txt">{it.text}</span>
                                    </li>
                                ))}
                            </ol>
                        </article>
                    ))}
                </div>
            </section>

            <section className="faq">
                <p className="eyebrow">ČASTÉ DOTAZY</p>
                <h2 className="sectionTitle center">Na co se studenti nejčastěji ptají</h2>
                <div className="faqGrid">
                    {faqs.map((f) => (
                        <article key={f.q} className="faqItem">
                            <h3>{f.q}</h3>
                            <p>{f.a}</p>
                        </article>
                    ))}
                </div>
            </section>

            <div className="linesHolder">
                <div className="lines">
                    <img src="images/pages/mainLines/line1.png" alt="" width={263} height={38} loading="lazy" />
                    <img src="images/pages/mainLines/line2.png" alt="" width={287} height={38} loading="lazy" />
                    <img src="images/pages/mainLines/line3.png" alt="" width={324} height={38} loading="lazy" />
                </div>
            </div>

            <section className="consult">
                <div className="inner">
                    <p className="eyebrow">Bez rizika</p>
                    <h2 className="sectionTitle">Nejsi si jistý? Nejdřív si promluvíme.</h2>
                    <p className="lead">
                        Domluv si nezávaznou konzultaci zdarma. Probereme, jak na tom jsi,
                        co Tě u přijímaček čeká, a společně najdeme nejlepší postup — bez jakýchkoli závazků.
                    </p>
                    <button className="button secondary" onClick={() => navigate("/kontakty", { state: { consultation: true } })}>
                        <p>Nezávazná konzultace zdarma</p>
                    </button>
                </div>
            </section>

            <section className="finalCta">
                <h2>Tvoje cesta na medicínu začíná tady</h2>
                <p>Připoj se k nám a připrav se na přijímačky chytře — ne ztěžka.</p>
                <button className="button" onClick={() => navigate("/nakup")}><p>Chci kurz</p></button>
            </section>
        </div>
    );
}
